<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { normalizeAttributeIcon } from 'dashboard/routes/dashboard/settings/attributes/iconOptions';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import AttributeBadge from 'dashboard/components-next/CustomAttributes/AttributeBadge.vue';

const props = defineProps({
  attribute: {
    type: Object,
    required: true,
  },
  badges: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['edit', 'delete']);
const { t } = useI18n();
const typeLabel = computed(() =>
  t(`ATTRIBUTES_MGMT.ATTRIBUTE_TYPES.${props.attribute.type?.toUpperCase()}`)
);

const iconByType = {
  text: 'i-ph-text-align-left',
  checkbox: 'i-ph-check-square',
  list: 'i-ph-list',
  date: 'i-ph-calendar',
  link: 'i-ph-link',
  number: 'i-ph-hash',
};

const attributeIcon = computed(() => {
  if (props.attribute.icon) return normalizeAttributeIcon(props.attribute.icon);
  const typeKey = props.attribute.type?.toLowerCase();
  return iconByType[typeKey] || 'i-ph-text-align-left';
});
</script>

<template>
  <div class="flex flex-col py-4 min-w-0">
    <div
      class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 min-w-0"
    >
      <div class="flex items-start sm:items-center gap-3 min-w-0 flex-1">
        <div
          class="flex items-center flex-shrink-0 size-10 justify-center rounded-2xl outline outline-1 outline-n-weak -outline-offset-1"
        >
          <Icon :icon="attributeIcon" class="size-4 text-n-slate-11" />
        </div>
        <div class="flex flex-col gap-1.5 items-start min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-2 min-w-0">
            <h4
              class="text-heading-3 break-words text-n-slate-12 min-w-0 max-w-full"
            >
              {{ attribute.label }}
            </h4>
            <div class="flex items-center gap-1.5">
              <Label :label="typeLabel" compact />
              <AttributeBadge
                v-for="badge in badges"
                :key="badge.type"
                :type="badge.type"
              />
            </div>
          </div>
          <div
            class="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-1.5 w-full"
          >
            <Icon icon="i-ph-key" class="size-3.5 text-n-slate-11" />
            <div class="flex flex-wrap items-center gap-2 min-w-0">
              <span
                class="text-body-main text-n-slate-11 break-words max-w-full"
              >
                {{ attribute.value }}
              </span>
              <template
                v-if="attribute.attribute_description || attribute.description"
              >
                <div class="w-px h-3 rounded-xl bg-n-weak flex-shrink-0" />
                <span
                  class="text-body-main text-n-slate-11 break-words max-w-full"
                >
                  {{ attribute.attribute_description || attribute.description }}
                </span>
              </template>
            </div>
          </div>
        </div>
      </div>
      <div class="flex gap-2 justify-end flex-shrink-0">
        <Button
          icon="i-ph-pencil-simple"
          :aria-label="$t('ATTRIBUTES_MGMT.EDIT.TITLE')"
          slate
          sm
          class="min-h-11 min-w-11"
          @click="emit('edit', attribute)"
        />
        <Button
          icon="i-ph-trash"
          :aria-label="$t('ATTRIBUTES_MGMT.DELETE.BUTTON_TEXT')"
          slate
          sm
          class="min-h-11 min-w-11 hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
          @click="emit('delete', attribute)"
        />
      </div>
    </div>
  </div>
</template>
